import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Wand2, ShieldAlert, ScrollText, Lightbulb, AlignLeft, BarChart3, Copy, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const RISK_STYLE = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-rose-50 text-rose-700 border-rose-200",
};

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={async () => {
        await navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      data-testid="ai-copy-btn"
    >
      {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

/* -------------------- SMART Coach -------------------- */
export function SmartCoachButton({ cycleId, fields, framework, onApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!cycleId) return toast.error("Save the cycle context first");
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/smart-coach", { cycle_id: cycleId, fields });
      setOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (out?.rewritten_fields && onApply) onApply(out.rewritten_fields);
    setOpen(false);
    toast.success("Applied SMART Coach rewrite");
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid="smart-coach-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-2" />}
        SMART Coach
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="smart-coach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" /> SMART Coach
            </DialogTitle>
            <DialogDescription>
              Score and rewrite for {framework?.name || "your framework"}.
            </DialogDescription>
          </DialogHeader>
          {loading || !out ? (
            <div className="py-12 flex items-center justify-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Reviewing your objective…
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                <div className="font-display text-3xl font-bold text-slate-900">{out.score}/10</div>
                <div className="text-xs uppercase tracking-wider text-slate-500">SMART score</div>
              </div>
              <div>
                <div className="axia-overline">Critique</div>
                <p className="text-sm text-slate-700 mt-1">{out.critique}</p>
              </div>
              {Object.keys(out.rewritten_fields || {}).length > 0 && (
                <div>
                  <div className="axia-overline">Suggested rewrite</div>
                  <div className="mt-2 space-y-2">
                    {Object.entries(out.rewritten_fields).map(([k, v]) => (
                      <div key={k} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`rewrite-${k}`}>
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{k}</div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(out.tips || []).length > 0 && (
                <div>
                  <div className="axia-overline">Tips</div>
                  <ul className="mt-1 space-y-1.5">
                    {out.tips.map((t, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {out && Object.keys(out.rewritten_fields || {}).length > 0 && (
            <DialogFooter>
              <Button onClick={apply} data-testid="smart-coach-apply">Apply rewrite</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- Metric Suggester -------------------- */
export function MetricSuggesterButton({ objectiveText, onPick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const run = async () => {
    if (!objectiveText || objectiveText.length < 4) return toast.error("Write the objective first");
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/metric-suggester", { objective_text: objectiveText });
      setItems(r.data.metrics || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid="metric-suggester-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5 mr-2" />}
        Suggest metrics
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="metric-suggester-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" /> Metric suggestions</DialogTitle>
            <DialogDescription>Pick one to copy into your "How will it be measured?" field.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Generating…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No suggestions returned.</div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {items.map((m, i) => (
                <button
                  key={i}
                  onClick={() => { onPick?.(`${m.metric} (${m.unit}) — ${m.target_example}`); setOpen(false); }}
                  className="w-full text-left axia-card p-3 hover:border-slate-400 transition"
                  data-testid={`metric-pick-${i}`}
                >
                  <div className="text-sm font-medium text-slate-900">{m.metric}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{m.unit} · target {m.target_example}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- Summary Generator -------------------- */
export function SummaryGeneratorButton({ reviewId, role, onApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");

  const run = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/summary", { review_id: reviewId, role });
      setText(r.data.summary || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid={`summary-gen-${role}-btn`}>
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ScrollText className="w-3.5 h-3.5 mr-2" />}
        Draft with AI
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="summary-gen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" /> Summary draft ({role})
            </DialogTitle>
            <DialogDescription>Pulled from objectives, check-ins, evidence and kudos. Always edit before submitting.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Composing your draft…
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-800 whitespace-pre-wrap max-h-[55vh] overflow-y-auto" data-testid="summary-gen-output">
                {text}
              </div>
              <DialogFooter className="gap-2">
                <CopyBtn text={text} />
                <Button onClick={() => { onApply?.(text); setOpen(false); toast.success("Draft inserted"); }} data-testid="summary-gen-apply">
                  Use draft
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- Bias Lens -------------------- */
export function BiasLensButton({ text, onSuggest }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!text || text.length < 10) return toast.error("Write at least one paragraph first");
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/bias-lens", { text });
      setOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid="bias-lens-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5 mr-2" />}
        Bias Lens
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bias-lens-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-violet-600" /> Bias Lens</DialogTitle>
            <DialogDescription>Heuristic check — always use your own judgement.</DialogDescription>
          </DialogHeader>
          {loading || !out ? (
            <div className="py-12 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Scanning…
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <span className={`axia-pill ${RISK_STYLE[out.overall_risk] || RISK_STYLE.low}`} data-testid="bias-overall-risk">
                Overall risk: {out.overall_risk}
              </span>
              {(out.flags || []).length === 0 ? (
                <div className="text-sm text-slate-500">No notable issues detected.</div>
              ) : (
                <ul className="space-y-3">
                  {out.flags.map((f, i) => (
                    <li key={i} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`bias-flag-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="axia-pill bg-slate-50 text-slate-700 border-slate-200">{f.type}</span>
                        {onSuggest && f.suggestion && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onSuggest(f.phrase, f.suggestion)} data-testid={`bias-apply-${i}`}>
                            Replace
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 text-sm">
                        <div className="text-rose-700 font-medium">"{f.phrase}"</div>
                        <div className="text-slate-600 mt-1">{f.rationale}</div>
                        <div className="text-emerald-700 mt-2"><span className="text-[11px] uppercase tracking-wider">Try</span> · {f.suggestion}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- 360 Theme Extractor -------------------- */
export function ThemesButton({ employeeId, count }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!count || count < 2) return toast.error("Need at least 2 responses to extract themes");
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/themes", { employee_id: employeeId || null });
      setOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid="themes-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <AlignLeft className="w-3.5 h-3.5 mr-2" />}
        Extract themes
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="themes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> 360 themes</DialogTitle>
            <DialogDescription>Aggregated from {out?.response_count ?? count ?? 0} responses. No respondent identities used.</DialogDescription>
          </DialogHeader>
          {loading || !out ? (
            <div className="py-12 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Aggregating…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
              <ThemeCol title="Strengths" items={out.strengths || []} accent="emerald" />
              <ThemeCol title="Growth areas" items={out.growth || []} accent="amber" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ThemeCol({ title, items, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : "text-amber-700";
  return (
    <div>
      <div className={`axia-overline ${cls}`}>{title}</div>
      <ul className="mt-2 space-y-2">
        {items.length === 0 && <li className="text-xs text-slate-400">None detected.</li>}
        {items.map((t, i) => (
          <li key={i} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`${accent === "emerald" ? "strength" : "growth"}-theme-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-display text-sm font-semibold text-slate-900">{t.label}</div>
              <span className="text-[11px] text-slate-500">{t.mentions} mention{t.mentions === 1 ? "" : "s"}</span>
            </div>
            <p className="text-xs text-slate-600 mt-1">{t.summary}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------- Review Quality -------------------- */
export function ReviewQualityButton({ reviewId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/ai/review-quality", { review_id: reviewId });
      setOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={loading} data-testid="review-quality-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
        Quality score
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="review-quality-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> Review quality</DialogTitle>
            <DialogDescription>Style + evidence audit, not a rating recommendation.</DialogDescription>
          </DialogHeader>
          {loading || !out ? (
            <div className="py-12 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Auditing…
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                <div className="font-display text-3xl font-bold text-slate-900" data-testid="quality-score">{out.score}/10</div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Quality score</div>
              </div>
              {(out.observations || []).length > 0 && (
                <div>
                  <div className="axia-overline">Observations</div>
                  <ul className="mt-1 space-y-1.5">
                    {out.observations.map((o, i) => (
                      <li key={i} className="text-sm text-slate-700">• {o}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(out.recommendations || []).length > 0 && (
                <div>
                  <div className="axia-overline">Recommendations</div>
                  <ul className="mt-1 space-y-1.5">
                    {out.recommendations.map((o, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" /> {o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
